import type { CatalogEntry, CatalogKind, ContentResponse, SceneView } from '@bioforge/protocol';
import { catalogArt, sceneArt } from '../art/art.js';
import { tr } from '../story/story.js';
import { button, el, overlayRoot, panel, picture } from './html.js';

/**
 * Сцены сюжета и карточки знакомства (SPEC.md, 15.7 и 19.3): по одной странице за раз,
 * любую можно пропустить. Временный вид — оформление с макетов на этапе 7.
 */

const KIND_LABEL: Readonly<Record<CatalogKind, string>> = {
  rule: 'Как играть',
  microbe: 'Новый микроб',
  modifier: 'Новая особенность',
  vessel: 'Новый сосуд',
  boss: 'Босс',
  'boss-ability': 'Способность босса',
  ability: 'Способность',
  attack: 'Атака',
  role: 'Роль',
  effect: 'Эффект',
};

export const MEDICAL_DISCLAIMER = 'Справка упрощена и не является медицинской рекомендацией.';

const STYLE_ID = 'bioforge-story-style';

/** Анимации сцен: появление реплик и «наезд камеры» во вступлении босса. */
function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = el('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes bf-line { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    @keyframes bf-zoom { from { transform: scale(1); } to { transform: scale(1.08); } }
    @keyframes bf-shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }
    .bf-line { animation: bf-line .3s ease-out both; }
    .bf-zoom svg { animation: bf-zoom 2.4s ease-out both, bf-shake .25s 2.4s 2; }
  `;
  document.head.appendChild(style);
}

/** Карточка знакомства или статья энциклопедии (без кнопок). */
export function cardBody(entry: CatalogEntry): HTMLDivElement {
  const body = el('div', { display: 'flex', flexDirection: 'column', gap: '10px' });
  const header = el('div', { display: 'flex', gap: '14px', alignItems: 'center' });
  const art = picture(catalogArt(entry), { width: '112px', flex: '0 0 112px' });
  const titles = el('div');
  titles.append(
    el(
      'div',
      { color: '#8cfff0', fontSize: '14px', textTransform: 'uppercase' },
      KIND_LABEL[entry.kind],
    ),
    el('h2', { margin: '4px 0 0', fontSize: '26px' }, tr(entry.title)),
  );
  header.append(art, titles);
  body.append(
    header,
    el('p', { margin: '0', fontSize: '18px', lineHeight: '1.4' }, tr(entry.what)),
    el(
      'p',
      { margin: '0', fontSize: '18px', lineHeight: '1.4', color: '#ffe9a8' },
      `Как справиться: ${tr(entry.how)}`,
    ),
  );
  if (entry.fact) {
    body.append(
      el('p', { margin: '0', color: '#9fc4d6', lineHeight: '1.4' }, `🔬 ${tr(entry.fact)}`),
    );
  }
  return body;
}

type Page =
  | { readonly kind: 'card'; readonly entry: CatalogEntry }
  | { readonly kind: 'frame'; readonly scene: SceneView; readonly frame: number };

export interface StoryOptions {
  readonly content: ContentResponse;
  readonly scene?: SceneView | null;
  readonly cards?: readonly CatalogEntry[];
  readonly reducedMotion: boolean;
}

/**
 * Показывает сцену, затем карточки; `done` — когда игрок досмотрел или пропустил.
 * Пока оверлей открыт, клавиши не доходят до экрана под ним.
 */
export class StoryOverlay {
  private readonly root = overlayRoot();
  private readonly box = panel('720px');
  private readonly pages: Page[] = [];
  private index = 0;
  /** Сколько реплик текущего кадра уже показано. */
  private shownLines = 0;
  private lines: HTMLDivElement | null = null;
  private resolve: (() => void) | null = null;
  readonly done: Promise<void>;
  private readonly onKey = (event: KeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.skip();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.next();
    }
  };

  constructor(private readonly options: StoryOptions) {
    ensureStyle();
    const scene = options.scene ?? null;
    if (scene) {
      scene.frames.forEach((_, frame) => this.pages.push({ kind: 'frame', scene, frame }));
    }
    const cards = options.cards ?? [];
    cards.forEach((entry) => this.pages.push({ kind: 'card', entry }));
    this.done = new Promise((resolve) => (this.resolve = resolve));
    if (this.pages.length === 0) {
      this.finish();
      return;
    }
    this.root.append(this.box);
    document.body.appendChild(this.root);
    window.addEventListener('keydown', this.onKey, true);
    this.render();
  }

  private render(): void {
    const page = this.pages[this.index];
    if (!page) return this.finish();
    this.box.replaceChildren();
    this.lines = null;
    if (page.kind === 'card') {
      this.box.style.width = 'min(560px, calc(100vw - 32px))';
      const more = this.pages.length - this.index - 1;
      const actions = el('div', {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
      });
      if (more > 0) {
        const skipAll = button('Пропустить все', '#24506b');
        skipAll.addEventListener('click', () => this.skip());
        actions.append(skipAll);
      }
      const ok = button('Понятно', '#1b7d74');
      ok.addEventListener('click', () => this.next());
      actions.append(ok);
      this.box.append(cardBody(page.entry), actions);
      ok.focus();
      return;
    }

    const { scene, frame } = page;
    const data = scene.frames[frame];
    if (!data) return this.finish();
    this.box.style.width = 'min(720px, calc(100vw - 32px))';
    const zoom = scene.kind === 'boss-intro' && !this.options.reducedMotion;
    const art = picture(sceneArt(data.art), { borderRadius: '12px', overflow: 'hidden' });
    if (zoom) art.className = 'bf-zoom';
    const heading = el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '12px',
      alignItems: 'baseline',
    });
    heading.append(
      el('h2', { margin: '0', fontSize: '22px' }, tr(scene.title)),
      el('span', { color: '#9fc4d6', fontSize: '14px' }, `${frame + 1} / ${scene.frames.length}`),
    );
    this.box.append(heading, art);
    if (data.caption) {
      this.box.append(
        el('p', { margin: '0', color: '#9fc4d6', fontStyle: 'italic' }, tr(data.caption)),
      );
    }
    this.lines = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      minHeight: '3em',
    });
    this.box.append(this.lines);
    const actions = el('div', { display: 'flex', gap: '12px', justifyContent: 'flex-end' });
    const skip = button('Пропустить', '#24506b');
    skip.addEventListener('click', () => this.skip());
    const next = button('Далее', '#1b7d74');
    next.addEventListener('click', () => this.next());
    actions.append(skip, next);
    this.box.append(actions);
    this.shownLines = 0;
    this.showLine();
    next.focus();
  }

  /** Следующая реплика кадра; false — все реплики уже показаны. */
  private showLine(): boolean {
    const page = this.pages[this.index];
    if (page?.kind !== 'frame' || !this.lines) return false;
    const line = page.scene.frames[page.frame]?.lines[this.shownLines];
    if (!line) return false;
    const speaker = this.options.content.speakers[line.speaker];
    const bubble = el('div', {
      padding: '10px 14px',
      borderRadius: '14px',
      background:
        line.speaker === 'doctor' ? '#1b4d3f' : line.speaker === 'forge' ? '#24384d' : '#4a2058',
      fontSize: '18px',
      lineHeight: '1.4',
    });
    if (!this.options.reducedMotion) bubble.className = 'bf-line';
    bubble.append(
      el('strong', { color: '#8cfff0' }, `${speaker ? tr(speaker) : line.speaker}: `),
      document.createTextNode(tr(line.text)),
    );
    this.lines.append(bubble);
    this.shownLines += 1;
    return true;
  }

  private next(): void {
    if (this.showLine()) return;
    this.index += 1;
    this.render();
  }

  /** Сцену пропускают целиком, карточки — все оставшиеся. */
  private skip(): void {
    const page = this.pages[this.index];
    if (page?.kind === 'frame') {
      while (this.pages[this.index]?.kind === 'frame') this.index += 1;
      this.render();
      return;
    }
    this.finish();
  }

  private finish(): void {
    window.removeEventListener('keydown', this.onKey, true);
    this.root.remove();
    this.resolve?.();
    this.resolve = null;
  }

  /** Закрывает без показа оставшегося (экран закрывается). */
  destroy(): void {
    this.finish();
  }
}
